// The pre-flight (F505, decision 112, after the eTLB's acceptance page):
// what the record says of the aircraft before the flight, a line each with
// a green or red bar, the report beside it, and the sign by the pilot who
// has the aircraft: the moment and a remark, kept on the booking.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import { api, queries, unwrap, type AirframeDetail, type MaintenancePage, type TechLogEntry } from "@/api";
import { Chip } from "@/components/Chip";
import { ErrorNote } from "@/components/ErrorNote";
import { rowFor } from "@/components/OnThisPc";
import { useHost } from "@/host/host";
import { hostQueries } from "@/host/queries";
import { formatDate, formatDateTime, formatRelative, pilotName } from "@/lib/format";

export const Route = createFileRoute("/airframes/$id/preflight")({
  component: Preflight,
});

type Tone = "ok" | "bad" | "plain";

type Line = { key: string; title: string; text: string; tone: Tone };

const LEVEL_WORDS = ["clean", "light", "noticeable", "heavy", "very heavy"];

/** The handover notes of the last week, newest first. */
function handovers(log: TechLogEntry[]): TechLogEntry[] {
  const since = Date.now() - 7 * 86_400_000;
  return log.filter((e) => e.kind === "handover" && new Date(e.at).getTime() >= since);
}

/** What this PC holds of the aircraft, under the companion: the entry installed, the platform's newest, whether the folder is here and whether the newest entry carries numbers only, no files (the sharing itself, decision 130). */
export type Installed = { seq: number | null; latest: number | null; onMachine?: boolean; numbersOnly?: boolean };

/** The lines of the summary, from the record alone; the entry installed from this PC when under the companion. */
export function preflightLines(a: AirframeDetail, p: MaintenancePage | undefined, log: TechLogEntry[], installed: Installed | null): Line[] {
  const lines: Line[] = [];
  if (installed) {
    const ok = installed.seq != null && installed.latest != null && installed.seq >= installed.latest;
    // Nothing installed and nothing to install: the platform's entry has no files yet, the aircraft flies with the files on this PC.
    const ownFiles = installed.seq == null && !!installed.numbersOnly;
    lines.push({
      key: "entry",
      title: "The entry installed on this PC",
      text:
        installed.seq == null
          ? ownFiles
            ? installed.onMachine
              ? `Entry ${installed.latest ?? 1} carries numbers only, no files: the aircraft flies with the files on this PC, and the first upload from here brings them to the platform.`
              : `Entry ${installed.latest ?? 1} carries numbers only, no files, and the aircraft is not in your X-Plane: the files come with the owner's first upload.`
            : "No entry of this aircraft is installed here: the booking installs the newest."
          : ok
            ? `Entry ${installed.seq}, the newest the platform holds.`
            : `Entry ${installed.seq} here, the platform holds entry ${installed.latest}: book the aircraft again to install it.`,
      tone: ok || (ownFiles && !!installed.onMachine) ? "ok" : "bad",
    });
  } else {
    lines.push({ key: "entry", title: "The entry to fly", text: `Entry ${a.ledger.latest_seq ?? "none yet"}, the newest the platform holds; the app installs it when you book.`, tone: "plain" });
  }
  const v = a.latest_version;
  lines.push({
    key: "parked",
    title: "Left parked and shut down",
    text: v ? (v.parked ? `Parked and shut down at the last return${a.ledger.last_pilot ? `, by ${a.ledger.last_pilot.name}` : ""}.` : v.on_gnd ? "On the ground but not shut down at the last return: expect it as it was left." : "In the air at the last return: the aircraft loads where it was.") : "No return yet: the aircraft as it was shared.",
    tone: v ? (v.parked ? "ok" : "bad") : "plain",
  });
  if (p) {
    const o = p.overview;
    lines.push({
      key: "defects",
      title: "Open defects",
      text: o.open_inspections > 0 ? `${o.open_inspections} inspection${o.open_inspections === 1 ? "" : "s"} due: a job closes it before the aircraft flies again.` : o.open_warnings > 0 ? `${o.open_warnings} warning${o.open_warnings === 1 ? "" : "s"} open, none blocking.` : "No defect open.",
      tone: o.open_inspections > 0 ? "bad" : "ok",
    });
    lines.push({
      key: "deferred",
      title: "Deferred under the MEL",
      text: o.deferred === 0 ? "Nothing deferred." : o.overdue_deferrals > 0 ? `${o.overdue_deferrals} of ${o.deferred} past the repair interval.` : `${o.deferred} deferred, within their intervals.`,
      tone: o.overdue_deferrals > 0 ? "bad" : "ok",
    });
    lines.push({
      key: "faults",
      title: "Failures on the aircraft",
      text: o.open_faults === 0 ? "None carried, none set at the next load." : `${o.open_faults} failure${o.open_faults === 1 ? "" : "s"} the aircraft carries or gets at the next load: see the maintenance page.`,
      tone: o.open_faults > 0 ? "bad" : "ok",
    });
  }
  const notes = handovers(log);
  lines.push({
    key: "handover",
    title: "The handover",
    text: notes.length === 0 ? "No note from the previous pilot this week." : `${notes.length} note${notes.length === 1 ? "" : "s"} from the previous pilot${notes[0]?.pilot ? `, the last by ${notes[0].pilot.name}` : ""}: read it on the right.`,
    tone: notes.length === 0 ? "plain" : "ok",
  });
  const c = a.ledger.condition;
  const worst = Math.max(c.level_windshield, c.level_exterior, c.level_cabin, c.level_cockpit ?? 0);
  lines.push({
    key: "condition",
    title: "The condition",
    text: c.profile === "off" ? "The condition model is off for this airframe." : `Windshield ${LEVEL_WORDS[c.level_windshield] ?? c.level_windshield}, exterior ${LEVEL_WORDS[c.level_exterior] ?? c.level_exterior}, cabin ${LEVEL_WORDS[c.level_cabin] ?? c.level_cabin}; a job on a parked aircraft cleans it.`,
    tone: c.profile === "off" ? "plain" : worst >= 3 ? "bad" : "ok",
  });
  return lines;
}

function Preflight() {
  const { id } = Route.useParams();
  const host = useHost();
  const airframe = useQuery(queries.airframe(id));
  const page = useQuery(queries.maintenance(id));
  const log = useQuery(queries.techLog(id));
  const me = useQuery(queries.me());
  const status = useQuery({ ...hostQueries.status(host!), enabled: !!host });
  const queryClient = useQueryClient();
  const [signing, setSigning] = useState(false);
  const [note, setNote] = useState("");
  const sign = useMutation({
    mutationFn: async (lid: string) => unwrap(await api.POST("/api/v1/leases/{lid}/preflight", { params: { path: { lid } }, body: { note: note.trim() || null } })),
    onSuccess: () => {
      setSigning(false);
      queryClient.invalidateQueries({ queryKey: ["airframe", id] });
      queryClient.invalidateQueries({ queryKey: ["airframes"] });
    },
  });
  if (airframe.isError) return <ErrorNote error={airframe.error} />;
  if (!airframe.data) return <p className="muted">Reading the record…</p>;
  const a = airframe.data;
  if (!a.your_role) return <p className="muted">The pre-flight is for the members.</p>;
  const lease = a.lease;
  const isHolder = !!lease && !!me.data && lease.holder.id === me.data.user.id;
  const row = host ? rowFor(status.data, id) : null;
  const installed: Installed | null = host
    ? { seq: row?.local?.installed?.seq ?? null, latest: row?.platform?.latest_seq ?? a.ledger.latest_seq ?? null, onMachine: !!row?.on_machine, numbersOnly: !!a.latest_version && !a.latest_version.sha256 }
    : null;
  const lines = preflightLines(a, page.data, log.data?.items ?? [], installed);
  const notes = handovers(log.data?.items ?? []);
  const open = (page.data?.tech_log ?? []).filter((e) => e.state !== "closed");
  const fluids = (page.data?.systems ?? []).flatMap((s) => s.components).filter((c) => /oil|oxygen|bottle|batter/.test(c.name));
  const signed = lease?.preflight_at ?? null;
  const bookedBy = lease ? `${lease.holder.name}, ${formatDateTime(lease.acquired_at)}` : "nobody";
  return (
    <section className="space-y-4" data-testid="preflight-page">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h2 className="text-[1.0714rem] font-semibold">Pre-flight</h2>
          <p className="muted text-xs">What the record says of {a.identity.reg} before the flight. The pilot who has the aircraft signs for it; the sign stays on the booking.</p>
        </div>
        <span className="flex-1" />
        {signed ? (
          <span className="pill pill-ok" data-testid="preflight-signed">
            signed {formatRelative(signed)} by {isHolder ? "you" : (lease?.holder.name ?? "the pilot")}
          </span>
        ) : isHolder ? (
          <button type="button" className="btn btn-primary" onClick={() => setSigning(true)} data-testid="preflight-sign">
            Sign the pre-flight
          </button>
        ) : lease ? (
          <span className="muted text-sm" data-testid="preflight-unsigned">
            {lease.holder.name} has the aircraft and has not signed yet.
          </span>
        ) : (
          <span className="muted text-sm" data-testid="preflight-unsigned">
            Nobody has the aircraft: book it from the app, then sign here.
          </span>
        )}
      </div>
      <div className="grid gap-4 lg:grid-cols-5">
        <div className="card space-y-1 lg:col-span-2" data-testid="preflight-summary">
          <h3 className="font-semibold">Summary</h3>
          <ul className="divide-y divide-line">
            {lines.map((l) => (
              <li key={l.key} className={`preflight-line preflight-${l.tone}`} data-testid={`preflight-${l.key}`} data-tone={l.tone}>
                <span className="font-semibold">{l.title}</span>
                <span className="muted block text-xs">{l.text}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="card space-y-4 lg:col-span-3" data-testid="preflight-report">
          <h3 className="font-semibold">Report</h3>
          <dl className="facts">
            <div className="fact">
              <dt className="muted text-xs">Aircraft</dt>
              <dd className="text-sm">
                {a.identity.reg}
                {a.name ? `, ${a.name}` : ""} · MSN {a.identity.msn}
              </dd>
            </div>
            <div className="fact">
              <dt className="muted text-xs">Last return</dt>
              <dd className="text-sm">{a.ledger.last_commit_at ? `entry ${a.ledger.latest_seq}, ${formatDateTime(a.ledger.last_commit_at)}${a.ledger.last_pilot ? ` by ${a.ledger.last_pilot.name}` : ""}` : "none yet"}</dd>
            </div>
            <div className="fact">
              <dt className="muted text-xs">Booked</dt>
              <dd className="text-sm">{bookedBy}</dd>
            </div>
            <div className="fact">
              <dt className="muted text-xs">Hours and flights</dt>
              <dd className="text-sm tnum">
                {a.ledger.total_hours.toFixed(1)} h · {a.ledger.cycles} flights
              </dd>
            </div>
            <div className="fact">
              <dt className="muted text-xs">Navigation data</dt>
              <dd className="text-sm tnum" data-testid="preflight-airac">
                AIRAC {a.ledger.airac ?? "unknown"}
              </dd>
            </div>
          </dl>
          <div>
            <h4 className="text-sm font-semibold">Handover from the previous pilot</h4>
            {notes.length === 0 ? (
              <p className="muted text-sm">None this week.</p>
            ) : (
              <ul className="mt-1 space-y-2" data-testid="preflight-handover-notes">
                {notes.map((n) => (
                  <li key={n.id} className="rounded border border-line bg-paper-2 p-2 text-sm">
                    <p className="whitespace-pre-line">{n.text}</p>
                    <p className="muted text-xs">
                      {n.pilot ? `${n.pilot.name}, ` : ""}
                      {formatDateTime(n.at)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <h4 className="text-sm font-semibold">Defects open and deferred</h4>
            {open.length === 0 ? (
              <p className="muted text-sm">Nil.</p>
            ) : (
              <ul className="mt-1 space-y-1 text-sm" data-testid="preflight-defect-list">
                {open.map((e) => (
                  <li key={e.id} className="flex flex-wrap items-center gap-2">
                    <Chip tone={e.state === "deferred" ? (e.overdue ? "bad" : "warn") : e.severity === "inspection" ? "bad" : e.severity === "warning" ? "warn" : undefined}>
                      {e.state === "deferred" ? `deferred${e.deferred_until ? ` until ${formatDate(e.deferred_until)}` : ""}` : e.severity === "inspection" ? "inspection due" : e.severity}
                    </Chip>
                    <span>{e.text}</span>
                  </li>
                ))}
              </ul>
            )}
            <Link to="/airframes/$id/maintenance" params={{ id }} className="muted mt-1 inline-block text-xs underline">
              The maintenance page
            </Link>
          </div>
          {fluids.length > 0 ? (
            <div>
              <h4 className="text-sm font-semibold">Fluids and bottles</h4>
              <ul className="mt-1 grid gap-1 text-sm sm:grid-cols-2" data-testid="preflight-fluids">
                {fluids.map((c) => (
                  <li key={c.name} className="flex items-baseline justify-between gap-2">
                    <span>{c.label}</span>
                    <span className="tnum">
                      {c.counter.toFixed(c.decimals || 0)} {c.unit}
                      {c.life != null ? ` of ${c.life.toFixed(0)}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {signed ? (
            <p className="text-sm" data-testid="preflight-sign-line">
              Signed {formatDateTime(signed)} by {lease?.holder.name}
              {lease?.preflight_note ? `: "${lease.preflight_note}"` : "."}
            </p>
          ) : null}
        </div>
      </div>
      {signing && lease && me.data ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4" role="dialog" aria-modal="true" aria-label="Sign the pre-flight" data-testid="preflight-dialog">
          <form
            className="card w-full max-w-md space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              sign.mutate(lease.id);
            }}
          >
            <h3 className="font-semibold">Sign the pre-flight</h3>
            <p className="muted text-sm">By signing you say you read what the record says of {a.identity.reg} on this page, and that you take the aircraft as it is for the flight.</p>
            <dl className="facts">
              <div className="fact">
                <dt className="muted text-xs">Signed by</dt>
                <dd className="text-sm">{pilotName(me.data.user)}</dd>
              </div>
              <div className="fact">
                <dt className="muted text-xs">Date and time</dt>
                <dd className="text-sm tnum">{formatDateTime(new Date().toISOString())}</dd>
              </div>
            </dl>
            <label className="flex flex-col gap-1">
              <span className="muted text-xs">A remark, if any</span>
              <textarea className="input w-full" rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Walk-around done, chocks out." aria-label="Your remark" data-testid="preflight-note" />
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn" onClick={() => setSigning(false)}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={sign.isPending} data-testid="preflight-confirm">
                Sign
              </button>
            </div>
            <ErrorNote error={sign.error} />
          </form>
        </div>
      ) : null}
    </section>
  );
}
